import { type MutableRefObject, useCallback, useMemo } from 'react';
import type { LngLat, YMap } from '@yandex/ymaps3-types';
import { List, ListItem, ListSection, Text } from '@direct-frontend/components';
import type { Nullable } from '@direct-frontend/stdlib';
import { useYandexMapsApi } from '@adv-frontend/yandex-maps-kit';
import { OrderDeliveryServicesTitles } from '@/shared/api/order';
import { type PickupPoint } from '@/shared/api/pickupPoint';
import { ServiceTypeIcon } from '@/shared/components/ServiceTypeIcon';
import { getMenuPosition } from './lib/getMenuPosition';
import { useMapViewport, Z_INDEX_CLUSTERS_MENU } from '../../lib';
import styles from './PickupAddressMapClusterMenu.module.css';

interface PickupAddressMapClusterMenuProps {
    ymapRef: MutableRefObject<Nullable<YMap>>;
    coordinates: LngLat;
    points: Array<PickupPoint>;
    onOptionClick: (point: PickupPoint, coordinated: LngLat) => void;
    sourceId: string;
    onClose: VoidFunction;
}

export const PickupAddressMapClusterMenu = (props: PickupAddressMapClusterMenuProps) => {
    const { ymapRef, coordinates, points, sourceId, onOptionClick, onClose } = props;

    const mapsApi = useYandexMapsApi();
    const { getMapBounds, getMapCenter } = useMapViewport({ ymapRef });

    const position = useMemo(() => {
        const bounds = getMapBounds();
        const center = getMapCenter();

        return getMenuPosition(coordinates, bounds, center);
    }, [coordinates, getMapCenter, getMapBounds]);

    const title = useMemo(() => {
        if (points.length) {
            return points[0].address;
        }

        return '';
    }, [points]);

    const handleClick = useCallback(
        (point: PickupPoint) => {
            onOptionClick(point, [point.position.longitude, point.position.latitude]);
            onClose();
        },
        [onClose, onOptionClick]
    );

    if (!mapsApi.maps || !mapsApi.addons?.defaultUITheme?.YMapPopupMarker || points.length === 0) {
        return null;
    }

    const { YMapListener } = mapsApi.maps;
    const { YMapPopupMarker } = mapsApi.addons.defaultUITheme;

    return (
        <>
            <YMapPopupMarker
                zIndex={Z_INDEX_CLUSTERS_MENU}
                source={sourceId}
                position={position}
                coordinates={coordinates}
                offset={10}
                hideOutsideViewport
                content={() => (
                    <div className={styles.menu}>
                        <List size="xs">
                            <ListSection>
                                <Text role="supplementary" className={styles.title}>
                                    {title}
                                </Text>
                            </ListSection>
                            <ListSection>
                                {points.map((point) => (
                                    <ListItem
                                        key={point.id}
                                        addonLeft={<ServiceTypeIcon serviceType={point.serviceType} size="20" />}
                                        onClick={() => handleClick(point)}
                                    >
                                        <Text className={styles.pointTitle}>
                                            Пункт выдачи {OrderDeliveryServicesTitles[point.serviceType]}
                                        </Text>
                                    </ListItem>
                                ))}
                            </ListSection>
                        </List>
                    </div>
                )}
            />

            <YMapListener onClick={onClose} onUpdate={onClose} />
        </>
    );
};
